import { CheckCircle2, Loader2, TriangleAlert } from "lucide-react";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatTimeAgo } from "@/shared/utils/dateTimeHelpers";

import type { PayoutRecheckRun } from "../../types/payments-wallets.types";

export interface PayoutRecheckNoticeProps {
  run: PayoutRecheckRun | null;
}

/** One line under the header: the providers are being asked about the payouts in progress, or what that found. */
export function PayoutRecheckNotice({ run }: PayoutRecheckNoticeProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  if (!run || run.state === "none") return null;

  if (run.state === "waiting" || run.state === "active") {
    return (
      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        {t("recheck_running")}
      </p>
    );
  }

  if (run.state === "failed" || !run.result) {
    return (
      <p className="mt-1 flex items-center gap-1.5 text-xs text-warning" role="status">
        <TriangleAlert className="size-3.5" aria-hidden />
        {t("recheck_failed")}
      </p>
    );
  }

  const { checked, settled, rejected, stillOpen, notFound, failed } = run.result;
  const when = run.finished_at ? formatTimeAgo(run.finished_at, { locale: activeLanguage }) : "";
  const parts =
    checked === 0
      ? [t("recheck_none")]
      : [
          t("recheck_done", { settled, rejected, stillOpen }),
          notFound > 0 ? t("recheck_not_found", { count: notFound }) : null,
          failed > 0 ? t("recheck_failed_some", { count: failed }) : null,
        ].filter(Boolean);

  return (
    <p
      className={cn(
        "mt-1 flex items-center gap-1.5 text-xs",
        notFound > 0 || failed > 0 ? "text-warning" : "text-muted-foreground",
      )}
      role="status"
    >
      <CheckCircle2 className="size-3.5 shrink-0" aria-hidden />
      <span>
        {parts.join(" · ")}
        {when ? ` · ${when}` : ""}
      </span>
    </p>
  );
}
